// ─── CHANGE LOGIN EMAIL ──────────────────────────────────────────────────────
// Self-service auth email change. Used by admin Settings, rep Profile, and
// customer Profile. Kept as a standalone component so each portal uses the
// SAME flow and UX copy — one place to maintain.
//
// Flow:
//   1. Show current login email (read-only).
//   2. User clicks "Change login email" → inline form appears.
//   3. User types new email → clicks "Send confirmation".
//   4. supabase.auth.updateUser({ email }) sends a link to the NEW email.
//   5. User clicks the link in their inbox → auth.users.email updates.
//   6. User signs out + back in with the new email (password unchanged).
//
// NOTE: If Supabase project has "Secure email change" enabled, a link ALSO
// goes to the OLD email and both must be confirmed. Admins whose old email
// is inactive (e.g. admin@mpcare.com) should either (a) temporarily disable
// Secure email change in Supabase Dashboard, or (b) change the email from
// Supabase Dashboard → Authentication → Users directly.

import React, { useState } from 'react';
import { changeLoginEmail } from './supabase';

export default function ChangeLoginEmail({ user, style }) {
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [status, setStatus] = useState({ kind: "idle", msg: "" });
  const currentEmail = user?.email || "";

  const handleSend = async () => {
    const next = (newEmail||"").trim().toLowerCase();
    if (!next) { setStatus({ kind:"error", msg:"Enter a new email address." }); return; }
    if (next === currentEmail.toLowerCase()) { setStatus({ kind:"error", msg:"That's already your login email." }); return; }
    setStatus({ kind:"busy", msg:"Sending confirmation…" });
    try {
      await changeLoginEmail(next);
      setStatus({ kind:"success", msg:`A confirmation link was sent to ${next}. Click it to complete the change, then sign out and sign back in with the new email (your password stays the same).` });
      setNewEmail("");
    } catch (e) {
      console.warn("changeLoginEmail failed:", e?.message);
      setStatus({ kind:"error", msg: e?.message || "Could not send confirmation. Try again." });
    }
  };

  const handleCancel = () => {
    setEditing(false); setNewEmail(""); setStatus({ kind:"idle", msg:"" });
  };

  const box = {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 18,
    ...style
  };
  const label = { fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "9px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, outline: "none" };
  const btnPrimary = { padding: "8px 16px", background: "#0B1F3A", color: "white", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" };
  const btnSecondary = { padding: "8px 16px", background: "white", color: "#374151", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" };

  const statusColor = status.kind === "error" ? "#B91C1C" : status.kind === "success" ? "#047857" : "#6B7280";
  const statusBg    = status.kind === "error" ? "#FEE2E2" : status.kind === "success" ? "#D1FAE5" : "#F3F4F6";

  if (!currentEmail) {
    return (
      <div style={box}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#0B1F3A", marginBottom: 4 }}>Login account</div>
        <div style={{ fontSize: 12, color: "#6B7280" }}>Sign in to manage your login email.</div>
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#0B1F3A", marginBottom: 2 }}>Login account</div>
      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 14 }}>The email you use to sign in to the portal.</div>
      <div style={{ marginBottom: 12 }}>
        <span style={label}>Current login email</span>
        <div style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>{currentEmail}</div>
      </div>
      {!editing ? (
        <button style={btnSecondary} onClick={()=>{ setEditing(true); setStatus({ kind:"idle", msg:"" }); }}>Change login email</button>
      ) : (
        <div>
          <span style={label}>New login email</span>
          <input
            type="email"
            style={input}
            value={newEmail}
            onChange={e=>setNewEmail(e.target.value)}
            placeholder="info@mpcare.com"
            disabled={status.kind === "busy"}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button style={btnPrimary} onClick={handleSend} disabled={status.kind === "busy"}>
              {status.kind === "busy" ? "Sending…" : "Send confirmation"}
            </button>
            <button style={btnSecondary} onClick={handleCancel} disabled={status.kind === "busy"}>Cancel</button>
          </div>
        </div>
      )}
      {status.msg && (
        <div style={{ marginTop: 12, padding: "10px 12px", background: statusBg, color: statusColor, borderRadius: 8, fontSize: 12, lineHeight: 1.5 }}>
          {status.msg}
        </div>
      )}
    </div>
  );
}
